import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { MAX_RULE_TEXT_LENGTH } from 'skipify-types';

import { Button } from '../../../../components/core';
import type { EllaRule } from '../../../../store/modules/merchant/types';
import { useOnboardingDraft } from '../OnboardingDraftContext';
export type ReadinessCheckItem = {
  id: string;
  title: string;
  description: string;
  textareaLabel: string;
  textareaPlaceholder: string;
};

export const READINESS_CHECK_ITEMS: readonly ReadinessCheckItem[] = [
  {
    id: 'return-policy',
    title: 'Return policy',
    description: 'No return policy found — one of the most common customer questions.',
    textareaLabel: 'Return policy',
    textareaPlaceholder: 'Describe your return policy for shoppers...',
  },
  {
    id: 'support-information',
    title: 'Support information / FAQs',
    description:
      'No support FAQs found — shoppers often ask about orders, returns, and account help.',
    textareaLabel: 'Support information / FAQs',
    textareaPlaceholder: 'Add support details and common FAQs for shoppers...',
  },
];

const READINESS_WORKFLOW_RULE_PREFIX = 'Readiness: ';

export const readinessWorkflowRuleName = (itemId: string): string => {
  const item = READINESS_CHECK_ITEMS.find((entry) => entry.id === itemId);
  return item
    ? `${READINESS_WORKFLOW_RULE_PREFIX}${item.title}`
    : `${READINESS_WORKFLOW_RULE_PREFIX}${itemId}`;
};

const getReadinessWorkflowRuleText = (rules: EllaRule[], itemId: string): string => {
  const ruleName = readinessWorkflowRuleName(itemId);
  return rules.find((rule) => (rule.name ?? '').trim() === ruleName)?.text ?? '';
};

export const upsertReadinessWorkflowRule = (
  rules: EllaRule[],
  itemId: string,
  text: string,
): EllaRule[] => {
  const ruleName = readinessWorkflowRuleName(itemId);
  const otherRules = rules.filter((rule) => (rule.name ?? '').trim() !== ruleName);

  if (!text.trim()) {
    return otherRules;
  }

  const existing = rules.find((rule) => (rule.name ?? '').trim() === ruleName);
  return [
    ...otherRules,
    {
      name: ruleName,
      text,
      enabled: existing?.enabled ?? true,
    },
  ];
};

export const hasReadinessWorkflowRuleText = (rules: EllaRule[], itemId: string): boolean =>
  Boolean(getReadinessWorkflowRuleText(rules, itemId).trim());

const getInitialExpandedIds = (rules: EllaRule[]) =>
  new Set(
    READINESS_CHECK_ITEMS.filter((item) => hasReadinessWorkflowRuleText(rules, item.id)).map(
      (item) => item.id,
    ),
  );

export const ReadinessCheckStep = (): JSX.Element => {
  const { draft, updateDraft } = useOnboardingDraft();
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(() =>
    getInitialExpandedIds(draft.rules),
  );

  const updateItemValue = (itemId: string, value: string) => {
    updateDraft((prev) => ({
      ...prev,
      rules: upsertReadinessWorkflowRule(prev.rules, itemId, value),
    }));
  };

  const openItem = (itemId: string) => {
    setExpandedIds((prev) => new Set(prev).add(itemId));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {READINESS_CHECK_ITEMS.map((item) => {
        const value = getReadinessWorkflowRuleText(draft.rules, item.id);
        const isExpanded = expandedIds.has(item.id);
        const isComplete = Boolean(value.trim());

        return (
          <Box
            key={item.id}
            data-testid={`ella-onboarding-readiness-item-${item.id}`}
            sx={{
              px: 2,
              py: 1.75,
              border: '1px solid',
              borderColor: 'border.main',
              borderRadius: '4px',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                <ErrorOutlineIcon
                  sx={{
                    fontSize: 20,
                    color: isComplete ? 'success.main' : 'warning.main',
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="small"
                    fontWeight="bold"
                    sx={{ lineHeight: '20px', mb: 0.5 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="small" color="text.secondary" sx={{ lineHeight: '18px' }}>
                    {isComplete
                      ? `Saved as workflow rule "${readinessWorkflowRuleName(item.id)}".`
                      : item.description}
                  </Typography>
                </Box>
              </Box>

              {!isExpanded ? (
                <Button
                  theme="secondary"
                  text="Add now"
                  onClick={() => openItem(item.id)}
                  dataTestId={`ella-onboarding-readiness-add-${item.id}`}
                  sx={{ flexShrink: 0, mt: 0.25, minWidth: 100, height: 40 }}
                />
              ) : null}
            </Box>

            {isExpanded ? (
              <Box sx={{ mt: 2, pl: 4.5 }}>
                <TextField
                  label={item.textareaLabel}
                  placeholder={item.textareaPlaceholder}
                  value={value}
                  onChange={(event) =>
                    updateItemValue(item.id, event.target.value.slice(0, MAX_RULE_TEXT_LENGTH))
                  }
                  fullWidth
                  multiline
                  minRows={4}
                  helperText={`${value.length}/${MAX_RULE_TEXT_LENGTH}`}
                  FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
                  inputProps={{
                    'data-testid': `ella-onboarding-readiness-input-${item.id}`,
                    maxLength: MAX_RULE_TEXT_LENGTH,
                  }}
                />
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};
